import { ArrowLeftIcon, ArrowRightIcon, ArrowsClockwiseIcon, CheckCircleIcon, GitCommitIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { api, q } from "./api.ts";
import { Provenance, useRefresh } from "./components.tsx";
import { FactPlaybooks } from "./fact-playbooks.tsx";
import { CategoryChecks } from "./product-profile.tsx";
import { categoryLabel, FACT_LABEL, FACT_STATUS, WORK_ORDER_LABELS, orderTone, PROFILE_DESIGN as D, SOURCE_REVIEW_LABELS as S } from "./labels.ts";
import { Button, Callout, EmptyState, ErrorText, Kbd, Pill, Reasons } from "./ui.tsx";
import { Chip } from "./ui/status.tsx";
import { Skeleton } from "./ui/skeleton.tsx";

// Sources and review (docs/design-system.md §5.12, §0.6; design/plan.md
// §4.10), the profile's "To review" tab: one fact at a time, as on the
// canvas. A proposed revision shows what the profile says now beside what the
// re-read proposes; a fact whose source changed without a proposal yet shows
// what changed, with the re-read limited to the changed sources. Nothing
// changes a fact until the person decides.

const OPEN = ["proposed", "approved", "running", "reconciling"];
const RUNNING = ["approved", "running", "reconciling"];

function valueText(value: unknown): string | null {
  if (Array.isArray(value)) return value.map((v) => categoryLabel(String(v))).join(", ") || null;
  return typeof value === "string" && value ? value : null;
}

/** Where a changed source can be looked at: the new commit, or the page. */
function changeHref(from: C.EvidenceItem, to: C.EvidenceItem | null) {
  const now = to ?? from;
  if (now.repository && now.commit) return `https://github.com/${now.repository}/commit/${encodeURIComponent(now.commit)}`;
  return now.url;
}

type Stale = C.SourceReview["stale"][number];
/** One thing to decide: a proposed revision (with the change under it, if known), or a changed source with no proposal yet. */
type Item = { key: string; candidate: C.FactCandidate | null; stale: Stale | null };

export function reviewItems(review: Pick<C.SourceReview, "stale" | "candidates">): Item[] {
  const proposed = review.candidates.map((candidate) => ({ key: candidate.key, candidate, stale: review.stale.find((s) => s.key === candidate.key) ?? null }));
  const waiting = review.stale.filter((s) => !review.candidates.some((c) => c.key === s.key)).map((stale) => ({ key: stale.key, candidate: null, stale }));
  return [...proposed, ...waiting];
}

/** What changed under a fact: each source, linked to the new commit or page. */
function Changes({ stale }: { stale: Stale }) {
  return <ul className="z-review-changes">
    {stale.changed.map((c) => {
      const href = changeHref(c.from, c.to);
      return <li key={c.from.id}><GitCommitIcon size={14} aria-hidden />{href ? <a className="z-link" href={href} target="_blank" rel="noreferrer noopener">{S.changed(c.from, c.to)}</a> : S.changed(c.from, c.to)}</li>;
    })}
    <li className="z-muted">{S.since(stale.stale_since)}</li>
  </ul>;
}

function Compare({ now, nowNote, proposed, proposedNote }: { now: string | null; nowNote?: string | null; proposed?: string | null; proposedNote?: string }) {
  return <div className="z-compare">
    <div className="z-compare-card">
      <span className="z-small z-muted">{S.nowInProfile}</span>
      <p className="z-fact-value">{now ?? <span className="z-muted">{S.noCurrent}</span>}</p>
      {nowNote && <span className="z-small z-muted">{nowNote}</span>}
    </div>
    {proposed !== undefined && <div className="z-compare-card is-proposed">
      <span className="z-small">{S.proposedValue}</span>
      <p className="z-fact-value">{proposed ?? <span className="z-muted">{S.removed}</span>}</p>
      {proposedNote && <span className="z-small">{proposedNote}</span>}
    </div>}
  </div>;
}

/** A proposed revision: the change, the two values side by side, and the three ways to decide. */
function CandidateReview({ candidate: c, stale, productId }: { candidate: C.FactCandidate; stale: Stale | null; productId: string }) {
  const refresh = useRefresh(productId);
  const categories = c.key === "categories";
  const start = () => (categories ? (Array.isArray(c.value) ? c.value.map(String) : Array.isArray(c.base.value) ? c.base.value.map(String) : []) : valueText(c.value) ?? valueText(c.base.value) ?? "");
  const [editing, setEditing] = useState<string | string[] | null>(null);
  const decide = useMutation({
    mutationFn: (body: C.DecideFactCandidateInput) => api.post<C.SourceReview>(`/api/fact-candidates/${c.id}/decision`, body),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (editing !== null) decide.mutate({ decision: "edit", value: typeof editing === "string" ? editing.trim() : editing });
  };
  const proposed = c.change === "update" ? valueText(c.value) : c.change === "remove" ? null : valueText(c.base.value);
  return <>
    {stale && <Changes stale={stale} />}
    <Compare now={valueText(c.base.value)} nowNote={c.base.status ? FACT_STATUS[c.base.status].label : null}
      proposed={proposed} proposedNote={S.change[c.change]} />
    <Provenance provenance={c.provenance} productId={productId} />
    {editing !== null ? (
      <form onSubmit={submit} className="z-form z-flow">
        {typeof editing === "string" ? (
          <textarea className="z-textarea" rows={3} aria-label={FACT_LABEL[c.key] ?? c.key} value={editing} onChange={(e) => setEditing(e.target.value)} maxLength={240} required autoFocus />
        ) : (
          <CategoryChecks value={editing} onChange={setEditing} />
        )}
        <div className="z-inline">
          <Button tone="primary" type="submit" busy={decide.isPending} disabled={Array.isArray(editing) && (editing.length < 1 || editing.length > 4)}>{S.saveEdit}</Button>
          <Button onClick={() => setEditing(null)}>{S.cancel}</Button>
        </div>
      </form>
    ) : (
      <div className="z-inline">
        <Button tone="primary" data-key="a" busy={decide.isPending && decide.variables?.decision === "accept"} onClick={() => decide.mutate({ decision: "accept" })}>{c.change === "unchanged" ? S.keepCurrent : S.acceptNew} <Kbd>A</Kbd></Button>
        {c.change !== "unchanged" && <Button busy={decide.isPending && decide.variables?.decision === "reject"} onClick={() => decide.mutate({ decision: "reject" })}>{S.keepCurrent}</Button>}
        <Button tone="ghost" icon={<PencilSimpleIcon />} disabled={decide.isPending} onClick={() => setEditing(start())}>{S.writeOwn}</Button>
      </div>
    )}
    <ErrorText error={decide.error} />
  </>;
}

/** A fact whose source changed with no proposal yet: what changed, the value it keeps meanwhile, and keep it or re-read. */
function StaleReview({ stale, value, status, productId, reread, onReread }: { stale: Stale; value: string | null; status: C.Fact["status"] | null; productId: string; reread: "can" | "running" | "no"; onReread: () => void }) {
  const refresh = useRefresh(productId);
  const keep = useMutation({ mutationFn: () => api.post<C.SourceReview>(`/api/products/${productId}/facts/${stale.key}/keep`), onSuccess: refresh });
  return <>
    <Changes stale={stale} />
    <Compare now={value} nowNote={status ? FACT_STATUS[status].label : null} />
    {reread !== "no" && <p className="z-small z-muted">{reread === "running" ? S.rereadRunning : S.rereadNote}</p>}
    <div className="z-inline">
      {reread === "can" && <Button tone="primary" data-key="a" onClick={onReread}>{S.reread} <Kbd>A</Kbd></Button>}
      <Button title={S.keepHelp} data-key={reread === "can" ? undefined : "a"} busy={keep.isPending} onClick={() => keep.mutate()}>{S.keep} {reread !== "can" && <Kbd>A</Kbd>}</Button>
    </div>
    <ErrorText error={keep.error} />
  </>;
}

export function SourceReview({ productId }: { productId: string }) {
  const refresh = useRefresh(productId);
  const product = useQuery(q.product(productId));
  // A check runs in a job: look again until its time stamp moves, for at most a minute.
  const [waiting, setWaiting] = useState<{ since: string | null; until: number } | null>(null);
  const review = useQuery({
    ...q.sourceReview(productId),
    refetchInterval: (query) => {
      const r = query.state.data;
      if (waiting && Date.now() < waiting.until && r?.checked_at === waiting.since) return 2000;
      return r?.order && RUNNING.includes(r.order.status) ? 3000 : false;
    },
  });
  const r = review.data;
  const orderStatus = r?.order?.status;
  // When a check lands or a re-read settles, everything that shows facts follows.
  useEffect(() => {
    if (waiting && r && r.checked_at !== waiting.since) {
      setWaiting(null);
      void refresh();
    }
  }, [r?.checked_at]);
  const lastStatus = useRef(orderStatus);
  useEffect(() => {
    const before = lastStatus.current;
    lastStatus.current = orderStatus;
    if (before && RUNNING.includes(before) && orderStatus && !RUNNING.includes(orderStatus)) void refresh();
  }, [orderStatus]);
  const check = useMutation({
    mutationFn: () => api.post<C.SourceReview>(`/api/products/${productId}/source-review/check`),
    onSuccess: (data) => setWaiting({ since: data.checked_at, until: Date.now() + 60_000 }),
  });
  const reread = useMutation({ mutationFn: () => api.post<C.SourceReview>(`/api/products/${productId}/source-review/reread`), onSuccess: refresh });
  const [position, setPosition] = useState(0);
  const card = useRef<HTMLElement>(null);
  // A decision removes the button that had focus; keep focus in the card so J/K/A go on working.
  const itemKey = review.data ? reviewItems(review.data)[Math.min(position, Math.max(0, reviewItems(review.data).length - 1))]?.key : undefined;
  useEffect(() => { if (document.activeElement === document.body) card.current?.focus(); }, [itemKey]);
  if (review.isLoading) return <Skeleton shape="text" />;
  if (!r) return <ErrorText error={review.error} />;
  const checking = Boolean(waiting) && r.checked_at === waiting?.since;
  const open = r.order && OPEN.includes(r.order.status) ? r.order : null;
  const items = reviewItems(r);
  const index = Math.min(position, Math.max(0, items.length - 1));
  const item = items[index];
  const rereadState = open && RUNNING.includes(open.status) ? "running" : r.stale.length > 0 && r.candidates.length === 0 ? "can" : "no";
  const fact = item ? product.data?.facts.find((f) => f.key === item.key) : undefined;
  const go = (delta: number) => setPosition(Math.min(items.length - 1, Math.max(0, index + delta)));
  // J/K move between items and A takes the main decision, only while focus is in the card and not in a field.
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if ((event.target as HTMLElement).closest("input, textarea, select")) return;
    const key = event.key.toLowerCase();
    if (key === "j") go(1);
    else if (key === "k") go(-1);
    else if (key === "a") card.current?.querySelector<HTMLButtonElement>('[data-key="a"]:not(:disabled)')?.click();
    else return;
    event.preventDefault();
  };
  return (
    <div className="z-flow">
      <div className="z-toolbar">
        <p className="z-inline">
          {r.status === "needs_review" ? <Chip variant="soft" tone="amber" dot>{S.status.needs_review}</Chip> : <span>{S.status[r.status]}</span>}
          {r.checked_at && <span className="z-small z-muted">{S.checked(r.checked_at)}</span>}
        </p>
        {r.checkable && (
          <Button size="sm" tone="ghost" busy={check.isPending || checking} onClick={() => check.mutate()}>
            <ArrowsClockwiseIcon size={14} /> {S.checkNow}
          </Button>
        )}
      </div>
      {!r.checkable && r.evidence.length > 0 && <p className="z-small z-muted">{S.localOnly}</p>}
      {r.check && r.check.status !== "ok" && (
        <Callout tone="warn">
          <p>{S.checkFailed}</p>
          <Reasons reasons={r.check.errors} />
        </Callout>
      )}
      {r.order && (open || r.order_reasons.length > 0) && <div className="z-flow">
        <p className="z-inline">
          <Pill tone={orderTone(r.order.status)} status>{WORK_ORDER_LABELS.orderStatus[r.order.status]}</Pill>
          <span className="z-small z-muted">{S.rereadOrder(r.order.revision, r.order.run?.started_at ?? null)}</span>
        </p>
        {r.order_reasons.length > 0 && <Reasons reasons={r.order_reasons} />}
      </div>}

      {item ? <section ref={card} className="z-review-card" aria-label={S.reviewTitle(FACT_LABEL[item.key] ?? item.key)} tabIndex={-1} onKeyDown={onKeyDown}>
        <div className="z-review-card-head">
          <h2>{S.reviewTitle(FACT_LABEL[item.key] ?? item.key)}</h2>
          {items.length > 1 && <span className="z-inline">
            <span className="z-small z-muted">{S.position(index + 1, items.length)}</span>
            <Button size="sm" tone="ghost" aria-label={S.previous} title={`${S.previous} (K)`} disabled={index === 0} onClick={() => go(-1)}><ArrowLeftIcon size={14} /></Button>
            <Button size="sm" tone="ghost" aria-label={S.next} title={`${S.next} (J)`} disabled={index === items.length - 1} onClick={() => go(1)}><ArrowRightIcon size={14} /></Button>
          </span>}
        </div>
        <FactPlaybooks productId={productId} factKey={item.key} />
        {item.candidate
          ? <CandidateReview key={item.candidate.id} candidate={item.candidate} stale={item.stale} productId={productId} />
          : item.stale && <StaleReview key={item.stale.fact_id} stale={item.stale} value={valueText(fact?.value)} status={fact?.status ?? null} productId={productId}
            reread={rereadState} onReread={() => reread.mutate()} />}
        <ErrorText error={reread.error} />
      </section> : <EmptyState title={D.nothingToReview} icon={<CheckCircleIcon size={24} />}>{S.note}</EmptyState>}
      {item?.candidate && <p className="z-small z-muted">{S.candidatesNote}</p>}

      <details className="z-disclosure">
        <summary>{S.evidence} <span className="z-count">{r.evidence.length}</span></summary>
        {r.evidence.length ? (
          <ul className="z-points z-small">
            {r.evidence.map((e) => (
              <li key={e.id}>
                {e.url ? <a className="z-link" href={e.url} target="_blank" rel="noreferrer noopener" style={{ overflowWrap: "anywhere" }}>{S.evidenceItem(e)}</a> : S.evidenceItem(e)}{" "}
                <span className="z-muted">· {S.checked(e.checked_at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="z-small z-muted">{S.evidenceEmpty}</p>
        )}
      </details>
      <ErrorText error={check.error} />
    </div>
  );
}
